import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ScaleLoader from "react-spinners/ScaleLoader";

import axiosApi from "../../api/axiosInstance"; // ✅ Use authenticated axios
import { ZulalWalletTopUp_V2_API_URL } from "../../utils/config";
import { UilWallet, UilCreateDashboard } from "@iconscout/react-unicons";

export const TopUpConfirmation = () => {
  // Custom hook for query string
  function useQuery() {
    const { search } = useLocation();
    return React.useMemo(() => new URLSearchParams(search), [search]);
  }
  const query = useQuery();
  const navigate = useNavigate();

  const [orderId, setOrderId] = useState("");
  const [loading, setLoading] = useState(false);

  // API state
  const [paymentState, setPaymentState] = useState(""); // "PURCHASED", "FAILED", etc.
  const [orderState, setOrderState] = useState(""); // "Created", "Failed"
  const [orderStatusMessage, setOrderStatusMessage] = useState("");
  const [paymentStatusMessage, setPaymentStatusMessage] = useState("");
  const [apiError, setApiError] = useState("");
  const [orderTotal, setOrderTotal] = useState(null);
  const [newBalance, setNewBalance] = useState(null);

  const hasProcessed = useRef(false); // ✅ Prevent duplicate calls

  // Utility
  function getLastNCharacters(str, n) {
    return str ? str.substr(Math.max(str.length - n, 0)).toUpperCase() : "";
  }

  // ✅ Get user-friendly error message
  const getUserFriendlyMessage = (errorResponse) => {
    const message = errorResponse?.message || "";
    const status = errorResponse?.status;

    // Transaction already used (duplicate submission)
    if (
      message.includes("already used") ||
      message.includes("already processed")
    ) {
      return "This order has already been completed. Please check your wallet balance.";
    }

    // Authentication errors
    if (status === 401) {
      return "Please log in to view this order.";
    }

    // Authorization errors
    if (status === 403) {
      return "You are not authorized to view this order.";
    }

    // Order not found
    if (status === 404 || message.includes("not found")) {
      return "Order not found. Please check your order reference.";
    }

    // Payment still processing
    if (status === 202 || message.includes("still being processed")) {
      return "Your payment is still being processed. Please wait a moment and check your wallet.";
    }

    // Payment failed
    if (
      message.includes("not successful") ||
      message.includes("Payment failed")
    ) {
      return "Payment was not successful. Please try again or contact support.";
    }

    // Service unavailable
    if (status === 503) {
      return "Payment system is temporarily unavailable. Please try again later.";
    }

    // Top-up failed
    if (message.includes("Top-up failed")) {
      return message; // Use the exact message from backend
    }

    // Return original message if no match
    return (
      message ||
      "There was an error processing your request. Please try again later."
    );
  };

  useEffect(() => {
    // ✅ Prevent duplicate API calls
    if (hasProcessed.current) {
      console.log("Already processed, skipping duplicate call");
      return;
    }

    const id = query.get("ref");

    //console.log("Received order ID from query string:", id);

    setOrderId(id || "");

    if (id && id.length === 36) {
      // ✅ Mark as processed before making the call
      hasProcessed.current = true;

      setLoading(true);
      setApiError(""); // Clear error before making request

      // ✅ Call backend endpoint
      axiosApi
        .get(`${ZulalWalletTopUp_V2_API_URL}${id}`)
        .then((response) => {
          const data = response.data;

          // ✅ Check if response exists
          if (!data) {
            throw new Error("No response received from server");
          }

          // ✅ Set state from backend response only
          setPaymentState(data.paymentState || "");
          setOrderState(data.orderState || "");
          setOrderTotal(data.orderTotal || null);
          setNewBalance(data.newBalance || null);

          // ✅ Check if successful
          if (data.success === true) {
            // Success case - use backend messages

            setOrderStatusMessage(
              data.message || "Your wallet has been topped up successfully!"
            );

            // Build payment status message
            let paymentMsg = "";

            if (data.paymentState === "PURCHASED") {
              paymentMsg = "Payment successful and wallet topped up!";
            } else if (data.paymentState === "AUTHORISED") {
              paymentMsg =
                "Payment authorized. Your wallet will be topped up shortly.";
            }
            setPaymentStatusMessage(paymentMsg);
          } else {
            // ✅ Failure case - use backend messages directly
            setOrderStatusMessage(
              data.message || "Top-up transaction was not completed"
            );
            setPaymentStatusMessage(
              data.paymentStatusMessage || "Payment was not processed"
            );
          }

          // Display the values for debugging
          // console.log("Order ID:", data.orderId || id);
          // console.log("Payment State:", data.paymentState);
          // console.log("Order State:", data.orderState);
          // console.log("Success:", data.success);
          // console.log("Message:", data.message);
          // console.log("Order Total:", data.orderTotal);
          // console.log("New Balance:", data.newBalance);
        })
        .catch((error) => {
          console.error("Error in Top-Up Confirmation:", error);
          // console.error("Error response:", error.response?.data);

          // ✅ Get user-friendly error message
          const errorMessage = getUserFriendlyMessage(
            error.response?.data || { status: error.response?.status }
          );

          setApiError(errorMessage);

          // ✅ Set failed states
          setPaymentState("FAILED");
          setOrderState("Failed");
          setOrderStatusMessage("Top-up transaction was not completed");
          setPaymentStatusMessage(errorMessage);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
      if (id) {
        setApiError("Invalid order reference. Please check your link.");
        setPaymentState("FAILED");
        setOrderState("Failed");
      }
    }
  }, [query]);

  // ✅ Success means: paymentState === "PURCHASED" && orderState === "Created"
  const isSuccess =
    (paymentState === "PURCHASED" || paymentState === "AUTHORISED") &&
    orderState === "Created";

  return (
    <div className="mb-60">
      <div className="flex flex-col mx-auto items-center justify-center text-center justify-items-center">
        <div>
          <ScaleLoader
            color="#36d7b7"
            height={40}
            loading={loading}
            radius={2}
            width={4}
          />
        </div>
        {loading && (
          <p className="mt-4 text-gray-600">
            Processing your payment... Please do not refresh this page.
          </p>
        )}
        {!loading && (
          <>
            {apiError ? (
              <div className="text-red-500 font-bold text-xl m-4">
                {apiError}
              </div>
            ) : isSuccess ? (
              <div className="flex flex-col md:flex-row mx-auto items-center justify-center text-center justify-items-center">
                <div>
                  <img
                    src="../../images/TopUpConfirmation.jpg"
                    width={450}
                    alt="Top Up confirmation"
                  />
                </div>
                <div className="max-w-md">
                  <span className="text-6xl m-3 font-bold text-center text-primary md:text-9xl md:font-extrabold md:text-left md:max-w-xl">
                    Thank You! <br />
                    <span className="text-2xl font-normal md:text-5xl m-3">
                      {orderStatusMessage}
                    </span>
                    <br />
                    <span className="text-xl font-normal text-green-600 md:text-3xl m-3">
                      {paymentStatusMessage}
                    </span>
                    <br />
                    <span className="text-lg font-normal text-secondary md:text-2xl m-3">
                      Reference ID: <b>{getLastNCharacters(orderId, 6)}</b>
                    </span>
                  </span>

                  <div className="flex gap-4 justify-center mt-6">
                    <button
                      type="button"
                      onClick={() => navigate("/user/dashboard")}
                      className="rounded-md bg-secondary px-3.5 py-2.5 text-center text-sm font-semibold text-textWhite shadow-sm hover:bg-secondaryHover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    >
                      Go to Dashboard
                    </button>

                    {/* Secondary Button - Wallet */}
                    <button
                      type="button"
                      onClick={() => navigate("/user/zulalwallet")}
                      className="rounded-md border border-primary px-3.5 py-2.5 text-center text-sm font-semibold text-primary shadow-sm hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                    >
                      Go to Wallet
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col md:flex-row mx-auto items-center justify-center text-center justify-items-center">
                <div>
                  <img
                    src="../../images/OrderCancel.jpg"
                    width={450}
                    alt="Top Up failed"
                  />
                </div>
                <div className="max-w-xl">
                  <span className="text-6xl m-3 font-bold text-center text-primary md:text-9xl md:font-extrabold md:text-left md:max-w-xl">
                    Sorry! <br />
                    <span className="text-2xl font-normal md:text-3xl m-3">
                      {orderStatusMessage ||
                        "Top-up transaction was not completed"}
                    </span>
                    <br />
                    <span className="text-xl font-normal text-red-500 md:text-3xl m-3">
                      {paymentStatusMessage || "Payment was not processed"}
                      <br />
                      Please contact our customer support team.
                      <br />
                      Reference ID:{" "}
                      <span className="text-xl font-bold text-secondary">
                        {getLastNCharacters(orderId, 6)}
                      </span>
                    </span>
                  </span>

                  <div className="flex gap-4 justify-center mt-6">
                    {/* Secondary Link - Home with Icon */}

                    {/* Secondary Link - Dashboard with Icon */}
                    <Link
                      to="/user/dashboard"
                      className="flex items-center text-primary hover:underline"
                    >
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-textWhite hover:bg-primaryHover focus:ring-2 focus:ring-secondary">
                        <UilCreateDashboard className="w-5 h-5" />
                      </div>
                      <span className="ml-2 text-sm font-semibold">
                        Go to Dashboard
                      </span>
                    </Link>

                    {/* Secondary Link - Wallet with Icon */}
                    <Link
                      to="/user/zulalwallet"
                      className="flex items-center text-primary hover:underline"
                    >
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-textWhite hover:bg-primaryHover focus:ring-2 focus:ring-secondary">
                        <UilWallet className="w-5 h-5" />
                      </div>
                      <span className="ml-2 text-sm font-semibold">
                        Go to Wallet
                      </span>
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
